import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Plus, Pencil, Trash2, Check, X, ArrowUp, ArrowDown } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

type BizType = {
  id: string;
  name: string;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
};

export function BusinessTypesContent() {
  const { toast } = useToast();
  const qc = useQueryClient();

  const { data: types = [], isLoading } = useQuery<BizType[]>({
    queryKey: ["/api/platform/business-types"],
    queryFn: async () => {
      const r = await fetch("/api/platform/business-types", { credentials: "include" });
      if (!r.ok) throw new Error("Failed to fetch");
      return r.json();
    },
  });

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [newName, setNewName] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  const deleteTarget = deleteTargetId ? types.find((t) => t.id === deleteTargetId) : null;

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["/api/platform/business-types"] });
  };

  const createMutation = useMutation({
    mutationFn: (name: string) =>
      apiRequest("POST", "/api/platform/business-types", { name }),
    onSuccess: () => {
      invalidate();
      setNewName("");
      setIsAdding(false);
      toast({ title: "Business type added" });
    },
    onError: async (err: any) => {
      const body = err?.response ? await err.response.json().catch(() => ({})) : {};
      toast({ title: body?.message || "Failed to add", variant: "destructive" });
    },
  });

  const renameMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      apiRequest("PATCH", `/api/platform/business-types/${id}`, { name }),
    onSuccess: () => {
      invalidate();
      setEditingId(null);
      setEditValue("");
      toast({ title: "Renamed" });
    },
    onError: async (err: any) => {
      const body = err?.response ? await err.response.json().catch(() => ({})) : {};
      toast({ title: body?.message || "Failed to rename", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      apiRequest("DELETE", `/api/platform/business-types/${id}`),
    onSuccess: () => {
      invalidate();
      setDeleteTargetId(null);
      toast({ title: "Business type removed" });
    },
    onError: () => toast({ title: "Failed to remove", variant: "destructive" }),
  });

  const reorderMutation = useMutation({
    mutationFn: ({ id, sortOrder }: { id: string; sortOrder: number }) =>
      apiRequest("PATCH", `/api/platform/business-types/${id}`, { sortOrder }),
    onSuccess: () => invalidate(),
    onError: () => toast({ title: "Failed to reorder", variant: "destructive" }),
  });

  const handleMoveUp = (idx: number) => {
    if (idx === 0) return;
    const current = types[idx];
    const above = types[idx - 1];
    reorderMutation.mutate({ id: current.id, sortOrder: above.sortOrder });
    reorderMutation.mutate({ id: above.id, sortOrder: current.sortOrder });
  };

  const handleMoveDown = (idx: number) => {
    if (idx === types.length - 1) return;
    const current = types[idx];
    const below = types[idx + 1];
    reorderMutation.mutate({ id: current.id, sortOrder: below.sortOrder });
    reorderMutation.mutate({ id: below.id, sortOrder: current.sortOrder });
  };

  const startEdit = (t: BizType) => {
    setEditingId(t.id);
    setEditValue(t.name);
  };

  const commitEdit = (id: string) => {
    if (!editValue.trim() || editValue.trim() === types.find((t) => t.id === id)?.name) {
      setEditingId(null);
      return;
    }
    renameMutation.mutate({ id, name: editValue.trim() });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-xl space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-sm">Business Types</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Used across the beta apply form, Prospects, and Customers spreadsheets. Changes take effect immediately — existing records keep their stored value.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setIsAdding(true)}
          data-testid="button-add-business-type"
        >
          <Plus className="w-3.5 h-3.5 mr-1.5" />
          Add Type
        </Button>
      </div>

      <div className="border border-border rounded-lg divide-y divide-border overflow-hidden">
        {types.map((t, idx) => (
          <div
            key={t.id}
            className="flex items-center gap-2 px-3 py-2.5 bg-card hover:bg-muted/30 group"
            data-testid={`row-business-type-${t.id}`}
          >
            {/* Reorder arrows */}
            <div className="flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={() => handleMoveUp(idx)}
                disabled={idx === 0 || reorderMutation.isPending}
                className="p-0.5 rounded hover:bg-muted disabled:opacity-30"
                data-testid={`button-move-up-${t.id}`}
              >
                <ArrowUp className="w-3 h-3" />
              </button>
              <button
                onClick={() => handleMoveDown(idx)}
                disabled={idx === types.length - 1 || reorderMutation.isPending}
                className="p-0.5 rounded hover:bg-muted disabled:opacity-30"
                data-testid={`button-move-down-${t.id}`}
              >
                <ArrowDown className="w-3 h-3" />
              </button>
            </div>

            {/* Name / edit */}
            <div className="flex-1">
              {editingId === t.id ? (
                <Input
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") commitEdit(t.id);
                    if (e.key === "Escape") setEditingId(null);
                  }}
                  autoFocus
                  className="h-7 text-sm"
                  data-testid={`input-rename-${t.id}`}
                />
              ) : (
                <span className="text-sm">{t.name}</span>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1">
              {editingId === t.id ? (
                <>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7"
                    onClick={() => commitEdit(t.id)}
                    disabled={renameMutation.isPending}
                    data-testid={`button-confirm-rename-${t.id}`}
                  >
                    {renameMutation.isPending ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Check className="w-3.5 h-3.5 text-green-600" />
                    )}
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7"
                    onClick={() => setEditingId(null)}
                    data-testid={`button-cancel-rename-${t.id}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </>
              ) : (
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7"
                    onClick={() => startEdit(t)}
                    data-testid={`button-edit-${t.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 text-destructive hover:text-destructive"
                    onClick={() => setDeleteTargetId(t.id)}
                    data-testid={`button-delete-${t.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        ))}

        {types.length === 0 && (
          <div className="py-8 text-center text-sm text-muted-foreground">
            No business types yet. Add one above.
          </div>
        )}

        {/* Add new inline */}
        {isAdding && (
          <div className="flex items-center gap-2 px-3 py-2.5 bg-muted/20">
            <div className="w-8" />
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && newName.trim()) createMutation.mutate(newName.trim());
                if (e.key === "Escape") { setIsAdding(false); setNewName(""); }
              }}
              placeholder="e.g. Circus Act"
              autoFocus
              className="h-7 text-sm flex-1"
              data-testid="input-new-business-type"
            />
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              onClick={() => newName.trim() && createMutation.mutate(newName.trim())}
              disabled={!newName.trim() || createMutation.isPending}
              data-testid="button-confirm-add-business-type"
            >
              {createMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Check className="w-3.5 h-3.5 text-green-600" />
              )}
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              onClick={() => { setIsAdding(false); setNewName(""); }}
              data-testid="button-cancel-add-business-type"
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        )}
      </div>

      {/* Delete confirm dialog */}
      <AlertDialog open={!!deleteTargetId} onOpenChange={(open) => { if (!open) setDeleteTargetId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove business type?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{deleteTarget?.name}</strong> will be hidden from all dropdowns. Existing records that already have this type stored will keep their value — this only affects future selections.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete-biz-type">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteTargetId && deleteMutation.mutate(deleteTargetId)}
              data-testid="button-confirm-delete-biz-type"
            >
              {deleteMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
